import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Backdrop, Box, ButtonBase } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import { DefaultLayoutZIndex } from "../../_utils/constraints";
import { useScreenSize } from "../_context/ScreenSizeProvider";
import { useLayout } from "../_context/DefaultProvider";

export const maxWidth = 400;
export const minWidth = 60;

const ExpandedButtonStyle = styled(motion(ButtonBase))(({ theme }) => ({
  zIndex: DefaultLayoutZIndex + 10,
  backgroundColor: theme.palette.primary.main,
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  right: "-20px",
  padding: 0,
  margin: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
  boxShadow: "0px 2px 5px rgba(0,0,0,0.2)",
}));

// Memoized static components
const SidebarHeader = memo(({ HeaderComponent, currentwidth, ...rest }) => (
  <Box
    sx={{
      width: "100%",
      boxSizing: "border-box",
      backgroundColor: (theme) => theme.palette.primary.main,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      color: (theme) => theme.palette.grey[100],
      borderBottom: `2px solid primary.main`,
      boxShadow: "0px 10px 10px -8px rgba(0,0,0,0.41)",
      paddingLeft: "5px",
      paddingRight: "5px",
      paddingTop: "10px",
      paddingBottom: "10px",
      borderRadius: "0 8px 0 0",
      boxSizing: "border-box",
    }}
  >
    {renderContent(HeaderComponent, currentwidth - 10, { ...rest })}
  </Box>
));

const SidebarContent = memo(({ ContentComponent, currentwidth, ...rest }) => (
  <Box
    sx={{
      flex: 1,
      width: "100%",
      backgroundColor: "transparent",

      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "8px",
      },
      "&::-webkit-scrollbar-track": (theme) => ({
        background: theme.palette.grey[300],
      }),
      "&::-webkit-scrollbar-thumb": (theme) => ({
        background: theme.palette.primary.main,
        borderRadius: "4px",
      }),
      "&::-webkit-scrollbar-thumb:hover": (theme) => ({
        background: theme.palette.primary.dark,
      }),
    }}
  >
    {renderContent(ContentComponent, currentwidth - 10, { ...rest })}
  </Box>
));

const SidebarFooter = memo(({ FooterComponent, currentwidth, ...rest }) => (
  <Box
    sx={{
      width: "100%",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: (theme) => theme.palette.grey[800],
      color: (theme) => theme.palette.grey[100],
      borderTop: (theme) => `2px solid ${theme.palette.grey[800]}`,
      boxShadow: (theme) => `0px -10px 10px -8px ${theme.palette.grey[800]}`,
      paddingLeft: "5px",
      paddingRight: "5px",
      paddingTop: "10px",
      paddingBottom: "10px",
      borderRadius: "0 0 8px 0",
      boxSizing: "border-box",
    }}
  >
    {renderContent(FooterComponent, currentwidth - 10, { ...rest })}
  </Box>
));

// Styled motion.div for cleaner JSX
const AnimatedSidebar = styled(motion.div)(({ theme }) => ({
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.paper,
  borderRight: `4px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[8],
  borderRadius: "0 8px 8px 0",
  color: theme.palette.grey[100],
  position: "fixed",
  top: 0,
  left: 0,
  height: "100vh",
  //overflow: "hidden",
  zIndex: DefaultLayoutZIndex + 4,
  display: "flex",
  flexDirection: "column",
}));

// Define the component
const SidebarToggleButton = memo(
  ({
    expandedButton,
    isSideBar,
    toggleExpansion,
    closeSidebar,
    currentwidth,
  }) => {
    const { drawerStates } = useLayout();
    return (
      <ExpandedButtonStyle
        onClick={(e) => {
          if (expandedButton) {
            toggleExpansion();
          } else {
            closeSidebar();
          }
        }}
        style={{
          padding: 0,
          margin: 0,
        }}
      >
        <motion.div
          initial={{ rotate: 0 }}
          animate={{
            rotate: (drawerStates?.isExpanded === true) === isSideBar ? 180 : 0,
          }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          exit={{ rotate: 0 }}
          style={{
            padding: 0,
            margin: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ChevronRightIcon
            style={{
              position: "relative",
              padding: 0,
              margin: 0,
              fontSize: "2rem",
            }}
            sx={{
              color: (theme) => theme.palette.grey[100],
            }}
          />
        </motion.div>
      </ExpandedButtonStyle>
    );
  }
);
const Sidebar = memo(
  ({
    currentwidth,
    translateX,
    isSideBar,
    closeSidebar,
    toggleExpansion,
    expandedButton,
    HeaderComponent = null,
    FooterComponent = null,
    ContentComponent = null,
  }) => {
    const sharedProps = { currentwidth, isSideBar, closeSidebar };

    return (
      <AnimatedSidebar
        initial={{
          opacity: 0,
          translateX: `calc(-100% - 20px)`,
          width: 0,
          visibility: "hidden",
        }}
        animate={{
          translateX,
          opacity: isSideBar ? 1 : 0,
          width: isSideBar ? currentwidth : 0,
          visibility: isSideBar ? "visible" : "hidden",
        }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 20,
        }}
        exit={{
          opacity: 0,
          translateX: `calc(-100% - 20px)`,
          width: 0,
          visibility: "hidden",
        }}
      >
        {HeaderComponent && (
          <SidebarHeader HeaderComponent={HeaderComponent} {...sharedProps} />
        )}
        {ContentComponent && (
          <SidebarContent
            ContentComponent={ContentComponent}
            {...sharedProps}
          />
        )}
        {FooterComponent && (
          <SidebarFooter FooterComponent={FooterComponent} {...sharedProps} />
        )}

        <SidebarToggleButton
          expandedButton={expandedButton}
          toggleExpansion={toggleExpansion}
          {...sharedProps}
        />
      </AnimatedSidebar>
    );
  }
);

export const renderContent = (Component, currentwidth, rest) => {
  // Case 1: If ContentComponent is a valid React element (a JSX element)
  if (React.isValidElement(Component)) {
    return React.cloneElement(Component, { currentwidth, ...rest });
  }

  // Case 2: If ContentComponent is a function or class component
  if (typeof Component === "function") {
    return <Component {...rest} currentwidth={currentwidth} />;
  }

  // Case 3: If ContentComponent is an HTML string (dangerouslySetInnerHTML)
  if (typeof Component === "string" && Component.startsWith("<")) {
    return <div dangerouslySetInnerHTML={{ __html: Component }} />;
  }
  // Case 4: If ContentComponent is a string, render as plain text
  if (typeof Component === "string") {
    return <div>{Component}</div>;
  }

  // Handle unsupported cases
  console.warn("Invalid component passed to renderContent.");
  return null;
};
const SidebarDrawer = ({
  drawerStatesKey = "",
  closeSidebar,
  expandedButton = true,
  HeaderComponent = null,
  FooterComponent = null,
  ContentComponent = null,
  ...rest
}) => {
  const { drawerStates, handleOpenDrawer, handleCloseDrawer } = useLayout();
  const { width } = useScreenSize();
  if (!drawerStates.hasOwnProperty(drawerStatesKey)) {
    return null;
  }
  const [collapsedWidth, setCollapsedWidth] = useState(maxWidth);
  const [expandedWidth, setExpandedWidth] = useState(minWidth);
  const [currentwidth, setCurrentwidth] = useState(maxWidth);
  const [translateX, setTranslateX] = useState(-1 * maxWidth);

  const toggleExpansion = () => {
    if (drawerStates?.isExpanded) {
      handleCloseDrawer("isExpanded");
    } else {
      handleOpenDrawer("isExpanded");
    }
  };

  useEffect(() => {
    // Check if the width or drawer state has actually changed to avoid unnecessary re-renders
    const newExpandedWidth = Math.min(
      Math.max(width * 0.7, minWidth),
      maxWidth
    );
    const newCollapsedWidth = newExpandedWidth * 0.3;
    const newCurrentWidth = drawerStates?.isExpanded
      ? newExpandedWidth
      : newCollapsedWidth;
    const newTranslateX = drawerStates[drawerStatesKey]
      ? 0
      : -(newCurrentWidth + 20);

    // Only update state if the values have actually changed
    if (expandedWidth !== newExpandedWidth) {
      setExpandedWidth(newExpandedWidth);
    }
    if (collapsedWidth !== newCollapsedWidth) {
      setCollapsedWidth(newCollapsedWidth);
    }
    if (currentwidth !== newCurrentWidth) {
      setCurrentwidth(newCurrentWidth);
    }
    if (translateX !== newTranslateX) {
      setTranslateX(newTranslateX);
    }
  }, [
    drawerStates,
    drawerStatesKey,
    width,
    expandedWidth,
    collapsedWidth,
    currentwidth,
    translateX,
    minWidth,
    maxWidth,
  ]);

  const handleClose = useCallback(() => {
    if (
      drawerStates.hasOwnProperty(drawerStatesKey) &&
      drawerStates[drawerStatesKey]
    ) {
      handleCloseDrawer(drawerStatesKey);
    }
  }, [drawerStatesKey, drawerStates]);
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        backgroundColor: "transparent",
      }}
      role="presentation" // Accessibility improvement
    >
      <Sidebar
        {...rest}
        expandedButton={expandedButton}
        toggleExpansion={toggleExpansion}
        collapsedWidth={collapsedWidth}
        expandedWidth={expandedWidth}
        currentwidth={currentwidth}
        translateX={translateX}
        isSideBar={drawerStates[drawerStatesKey]}
        closeSidebar={handleClose}
        HeaderComponent={HeaderComponent}
        ContentComponent={ContentComponent}
        FooterComponent={FooterComponent}
      />
      <Backdrop
        sx={{
          width: "100%",
          minHeight: "100vh",
          backgroundColor: "transparent",
          zIndex: DefaultLayoutZIndex + 3,
        }}
        open={drawerStates[drawerStatesKey]}
        onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}
      ></Backdrop>
    </Box>
  );
};

export default memo(SidebarDrawer);
